import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { Canvas, useFrame } from '@react-three/fiber';
import ScreenTitle from '../components/ScreenTitle';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #1a0d00 0%, #4d2600 100%);
  padding: 20px;
  display: flex;
  flex-direction: column;
  position: relative;
`;


const TimelineContainer = styled.div`
  position: relative;
  height: 60%;
  width: 100%;
  margin-bottom: 30px;
`;

const Timeline = styled.div`
  position: absolute;
  top: 50%;
  left: 10%;
  right: 10%;
  height: 4px;
  background: linear-gradient(to right, #ffcc00, #ff6600);
  border-radius: 2px;
`;

const TimeMarker = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${props => props.position}%;
  width: 3px;
  height: 60px;
  background: white;
  
  &::after {
    content: '${props => props.year}';
    position: absolute;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    font-size: 0.8rem;
    white-space: nowrap;
  }
`;

const ScenarioPath = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const ScenarioLabel = styled.div`
  position: absolute;
  color: ${props => props.color};
  font-size: 0.9rem;
  font-weight: bold;
  left: ${props => props.x}%;
  top: ${props => props.y}%;
  transform: translate(-50%, -50%);
  white-space: nowrap;
  pointer-events: none;
`;

const ScenarioCards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  height: 35%;
`;

const ScenarioCard = styled.div`
  background: rgba(0, 0, 0, 0.6);
  border: 2px solid ${props => props.color};
  border-radius: 12px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.color};
    box-shadow: 0 10px 30px ${props => props.color}40;
  }
`;

const CardTitle = styled.h3`
  color: ${props => props.color};
  font-size: 1rem;
  margin-bottom: 8px;
`;

const CardDescription = styled.p`
  color: white;
  font-size: 0.8rem;
  line-height: 1.4;
`;

const BackgroundCanvas = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.3;
  z-index: 0;
`;

function FloatingParticles() {
  const groupRef = useRef();
  
  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.1;
    }
  });

  return (
    <group ref={groupRef}>
      {[...Array(20)].map((_, i) => (
        <mesh key={i} position={[
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 10,
          (Math.random() - 0.5) * 20
        ]}>
          <sphereGeometry args={[0.1, 8, 8]} />
          <meshBasicMaterial color="#ffcc00" opacity={0.6} transparent />
        </mesh>
      ))}
    </group>
  );
}

function FuturesScreen() {
  const [selectedScenario, setSelectedScenario] = useState(null);

  const scenarios = [
    {
      id: 1,
      name: 'Baseline 2040',
      color: '#0099ff',
      description: 'Nykyinen kehityskulku jatkuu. Hidas kasvu, asteittainen muutos, perinteiset instituutiot säilyvät.',
      probability: 35
    },
    {
      id: 2,
      name: 'Häviävä Kansallisvaltio',
      color: '#ffcc00',
      description: 'Paikallisuus vahvistuu, globalisaatio hidastuu. Alueelliset erot kasvavat, demokratiainnovaatiot paikallisesti.',
      probability: 22
    },
    {
      id: 3,
      name: 'Onnistunut Muutos',
      color: '#00ff88',
      description: 'Nopea digitaalinen ja vihreä siirtymä. Vahva EU, teknologia-ajama kasvu, uudet osallistumismuodot.',
      probability: 28
    },
    {
      id: 4,
      name: 'Ontto Eurooppa',
      color: '#ff0066',
      description: 'Pintapuolinen yhtenäisyys, kasvavat erot. Teknokraattinen ohjaus, nuorten tyytymättömyys.',
      probability: 15
    }
  ];

  return (
    <Container>
      <BackgroundCanvas>
        <Canvas camera={{ position: [0, 0, 10] }}>
          <ambientLight intensity={0.2} />
          <FloatingParticles />
        </Canvas>
      </BackgroundCanvas>
      
      <ScreenTitle>
        EUROOPAN TULEVAISUUSSKENAARIOITA 2040
      </ScreenTitle>
      
      <TimelineContainer>
        <Timeline />
        <TimeMarker position={0} year="2022" />
        <TimeMarker position={20} year="2025" />
        <TimeMarker position={40} year="2030" />
        <TimeMarker position={60} year="2035" />
        <TimeMarker position={80} year="2040" />
        
        {/* Probability-weighted cone visualization */}
        <ScenarioPath viewBox="0 0 100 100">
          {/* Uncertainty cone background */}
          <path
            d="M 10 50 L 90 20 L 90 80 Z"
            fill="rgba(255, 255, 255, 0.05)"
            stroke="rgba(255, 255, 255, 0.2)"
            strokeWidth="0.2"
          />
          
          {/* Present moment line */}
          <line
            x1="10" y1="50"
            x2="10" y2="50"
            stroke="white"
            strokeWidth="0.5"
            opacity="1"
          />
          
          {/* Scenario trajectories - positioned by probability (35%, 28%, 22%, 15%) */}
          {/* Baseline 35% - center/dominant path */}
          <path d="M 10 50 Q 35 50 60 50 T 90 50" stroke="#0099ff" strokeWidth="0.6" fill="none" opacity="1" />
          
          {/* Onnistunut 28% - second most likely */}
          <path d="M 10 50 Q 35 47 60 42 T 90 35" stroke="#00ff88" strokeWidth="0.5" fill="none" opacity="0.9" />
          
          {/* Häviävä 22% - third */}
          <path d="M 10 50 Q 35 53 60 58 T 90 65" stroke="#ffcc00" strokeWidth="0.4" fill="none" opacity="0.8" />
          
          {/* Ontto 15% - least likely, thinnest line */}
          <path d="M 10 50 Q 35 56 60 68 T 90 80" stroke="#ff0066" strokeWidth="0.3" fill="none" opacity="0.7" />
        </ScenarioPath>
        
        {/* Labels positioned by probability weight */}
        <ScenarioLabel x={92} y={50} color="#0099ff">Baseline (35%)</ScenarioLabel>
        <ScenarioLabel x={92} y={35} color="#00ff88">Onnistunut (28%)</ScenarioLabel>
        <ScenarioLabel x={92} y={65} color="#ffcc00">Häviävä (22%)</ScenarioLabel>
        <ScenarioLabel x={92} y={80} color="#ff0066">Ontto (15%)</ScenarioLabel>
      </TimelineContainer>

      <ScenarioCards>
        {scenarios.map(scenario => (
          <ScenarioCard
            key={scenario.id}
            color={scenario.color}
            onClick={() => setSelectedScenario(scenario)}
          >
            <CardTitle color={scenario.color}>{scenario.name}</CardTitle>
            <CardDescription>{scenario.description}</CardDescription>
            <div style={{ 
              marginTop: '10px', 
              color: scenario.color, 
              fontWeight: 'bold' 
            }}>
              {scenario.probability}% todennäköisyys
            </div>
          </ScenarioCard>
        ))}
      </ScenarioCards>
    </Container>
  );
}

export default FuturesScreen;